import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SignInBrandLogo } from '@l2u/web-shared';
import { ErrorMessage } from '@l2u/web-shared';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { PoweredByFooter } from '@l2u/web-shared';
import { ApiError, publicApi } from '@l2u/web-shared';
import {
  SignInButton,
  SignInField,
  SignInHeading,
  SignInHelp,
  SignInInput,
  SignInLayout,
  SignInNote,
  SignInRule,
} from '@l2u/web-shared';

/**
 * "Forgot your password?" — the first screen behind the admin sign-in's reset
 * link, and the reason it is here rather than left on the old card: that link
 * sits in the password field's label row now, so a user is one click from the
 * redesign at all times. A screen that changes ground colour, type family and
 * layout mid-flow reads as a different site, which on an auth flow reads as
 * phishing.
 */
export function ForgotPassword() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  // Only ever shown after the API answered `workspace_required`: on a tenant's
  // own admin host (every deployed environment) the API reads the workspace
  // off the browser's Origin and this field never appears. It exists for a
  // slug-less admin host, where nothing else can say which workspace is meant.
  const [askWorkspace, setAskWorkspace] = useState(false);
  const [workspace, setWorkspace] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      // The API mints the recovery link and emails it through our own mail
      // path; the link it builds is the tenant's admin host, never this page's
      // origin. It answers `{ sent: true }` whether or not the address is an
      // admin here, so there is nothing to read from the response.
      const slug = normaliseWorkspace(workspace);
      await publicApi.post('/api/auth/admin/password-reset', {
        email: email.trim(),
        ...(slug ? { tenant_slug: slug } : {}),
      });
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'workspace_required' && !askWorkspace) {
        setAskWorkspace(true);
      } else {
        // Transport, validation or server trouble only — never anything about
        // the address. Shown as the generic error it is.
        setError(err);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <SignInLayout audience="admin">
      <SignInBrandLogo />

      <SignInHeading eyebrow="Admin portal" title="Forgot your password?">
        {sent
          ? 'Check your inbox for a reset link.'
          : 'Enter your email address and we’ll send you a link to set a new password.'}
      </SignInHeading>

      {sent ? (
        <>
          {/* "If an admin account exists for" is load-bearing, exactly as it is
              on the portal's code step: the reset endpoint is unauthenticated
              and takes an address, so a confirmation
              that distinguished "sent" from "no such admin" would turn this
              page into an enumeration oracle over a customer's staff list. */}
          <SignInNote>
            If an admin account exists for <strong className="font-semibold">{email.trim()}</strong>
            , we’ve emailed a link to reset your password. Check your inbox, and your spam folder.
          </SignInNote>
          <SignInRule />
          <SignInHelp>
            <div>
              <Link to="/login">Back to sign in</Link>
            </div>
            <PoweredByFooter />
          </SignInHelp>
        </>
      ) : (
        <>
          <form className="flex flex-col gap-4" onSubmit={onSubmit}>
            <SignInField label="Email address" audience="admin">
              <SignInInput
                audience="admin"
                type="email"
                autoComplete="username"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com.au"
              />
            </SignInField>

            {askWorkspace && (
              <SignInField label="Workspace" audience="admin">
                <SignInInput
                  audience="admin"
                  type="text"
                  autoComplete="organization"
                  value={workspace}
                  onChange={(e) => setWorkspace(e.target.value)}
                  required
                  placeholder="your-company"
                />
              </SignInField>
            )}

            <div role="alert" className="empty:hidden">
              <ErrorMessage error={error} />
            </div>

            <SignInButton
              audience="admin"
              type="submit"
              loading={submitting}
              disabled={!email.trim() || (askWorkspace && !normaliseWorkspace(workspace))}
            >
              Send reset link
            </SignInButton>
          </form>

          <SignInRule />

          <SignInHelp>
            <div>
              <Link to="/login">Back to sign in</Link>
            </div>
            <PoweredByFooter />
          </SignInHelp>
        </>
      )}
    </SignInLayout>
  );
}

/**
 * What a person types for a workspace, reduced to a slug: `acme`,
 * `ACME`, `acme.admin-compliance.example.com` and `https://acme.…/login` all
 * mean `acme`. The API resolves it only when the request's Origin could not.
 */
function normaliseWorkspace(input: string): string {
  const host = input.trim().toLowerCase().replace(/^[a-z]+:\/\//, '').split('/')[0] ?? '';
  return host.split('.')[0] ?? '';
}
